import React, { useState, useEffect, useMemo } from 'react';
import { Modal, Table, Typography, Tag, Divider } from 'antd';
import { useSelector } from 'react-redux';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../../../config/firebase';
import { secureGetItem as getSecureItem } from '../../../data/utils';

const { Title, Text } = Typography;

const OrderDetailsModal = ({ visible, onCancel, order, userProfiles = {} }) => {
    // Get catalogues from Redux and user profiles
    const catalogues = useSelector(state => state.catalogues.listaCatalogues || []);

    // State for catalogue name when not found in Redux
    const [catalogueName, setCatalogueName] = useState('');
    const [loadingCatalogue, setLoadingCatalogue] = useState(false);

    // State for user role
    const [currentUserRole, setCurrentUserRole] = useState(null);

    // State for virtual scrolling
    const [displayedItems, setDisplayedItems] = useState([]);
    const [hasMore, setHasMore] = useState(true);
    const [loadingMore, setLoadingMore] = useState(false);
    const ITEMS_PER_LOAD = 50;

    // Function to get user full name from email
    const getUserFullName = (email, userProfiles) => {
        if (!email || !userProfiles[email]) {
            return 'Unknown User';
        }

        const profile = userProfiles[email];
        const fullName = `${profile.firstName} ${profile.lastName}`.trim();
        return fullName || 'Unknown User';
    };

    // Function to get catalogue name by ID
    const getCatalogueName = async (catalogueId) => {
        if (!catalogueId) return 'Unknown Catalogue';

        // First, try to find in Redux
        const catalogue = catalogues.find(cat => cat.id === catalogueId);
        if (catalogue?.nameCatalog) {
            return catalogue.nameCatalog;
        }

        // If not found in Redux, fetch from Firestore
        try {
            setLoadingCatalogue(true);
            const catalogueDoc = await getDoc(doc(db, 'Catalogues', catalogueId));
            if (catalogueDoc.exists()) {
                const catalogueData = catalogueDoc.data();
                return catalogueData.nameCatalog || `Catalogue ${catalogueId.substring(0, 8)}...`;
            }
        } catch (error) {
            console.error('Error fetching catalogue:', error);
        } finally {
            setLoadingCatalogue(false);
        }

        return `Catalogue ${catalogueId.substring(0, 8)}...`;
    };

    // Effect to load user role
    useEffect(() => {
        const loadUserRole = async () => {
            try {
                const storedRole = await getSecureItem('role');
                if (storedRole && storedRole !== 'null' && storedRole !== '') {
                    setCurrentUserRole(storedRole);
                } else {
                    setCurrentUserRole(null);
                }
            } catch (error) {
                setCurrentUserRole(null);
            }
        };

        loadUserRole();
    }, []);

    // Effect to load catalogue name when order changes
    useEffect(() => {
        const loadCatalogueName = async () => {
            if (order?.catalogRef) {
                const name = await getCatalogueName(order.catalogRef);
                setCatalogueName(name);
            }
        };

        if (visible && order) {
            loadCatalogueName();
        }
    }, [visible, order, catalogues]);

    // Function to format timestamp
    const formatTimestamp = (timestamp) => {
        if (!timestamp) return 'N/A';

        let date;
        if (timestamp.__time__) {
            date = new Date(timestamp.__time__);
        } else if (timestamp.seconds) {
            date = new Date(timestamp.seconds * 1000);
        } else if (timestamp instanceof Date) {
            date = timestamp;
        } else {
            date = new Date(timestamp);
        }

        return date.toLocaleString('en-GB', {
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    // Function to get status colour (uniformato con OrderPage.js)
    const getStatusColour = (status) => {
        switch (status) {
            case 'pending_analysis':
                return 'processing';
            case 'analysis_complete':
                return 'success';
            case 'processing_addition':
                return 'warning';
            case 'quote_requested':
                return 'purple';
            case 'quote_sent':
                return 'cyan';
            case 'quote_accepted':
                return 'green';
            case 'quote_rejected':
                return 'red';
            case 'completed':
                return 'green';
            case 'error':
                return 'error';
            default:
                return 'default';
        }
    };

    // Function to get status display text (uniformato con OrderPage.js)
    const getStatusText = (status) => {
        switch (status) {
            case 'pending_analysis':
                return 'Pending Analysis';
            case 'analysis_complete':
                return 'Analysis Complete';
            case 'processing_addition':
                return 'Processing Addition';
            case 'quote_requested':
                return 'Quote Requested';
            case 'quote_sent':
                return 'Quote Sent';
            case 'quote_accepted':
                return 'Quote Accepted';
            case 'quote_rejected':
                return 'Quote Rejected';
            case 'completed':
                return 'Completed';
            case 'error':
                return 'Error';
            default:
                return status;
        }
    };

    // Columns for ADD ASSETS (Available items with catalogue name)
    const addAssetsColumns = [
        {
            title: 'SKU',
            dataIndex: 'skuCode',
            key: 'skuCode',
            width: '25%',
            render: (text) => (
                <Text>
                    {text || 'N/A'}
                </Text>
            ),
        },
        {
            title: 'EAN',
            dataIndex: 'eanCode',
            key: 'eanCode',
            width: '25%',
            render: (text) => (
                <Text>
                    {text || 'N/A'}
                </Text>
            ),
        },
        {
            title: 'UPC',
            dataIndex: 'upcCode',
            key: 'upcCode',
            width: '25%',
            render: (text) => (
                <Text>
                    {text || 'N/A'}
                </Text>
            ),
        },
        {
            title: 'Catalogue Name',
            key: 'catalogueName',
            width: '25%',
            render: (_, record) => {
                return (
                    <Tag color="blue">
                        {loadingCatalogue ? 'Loading...' : (catalogueName || 'Unknown Catalogue')}
                    </Tag>
                );
            },
        },
    ];

    // Columns for ORDER ASSETS (Orderable items)
    const orderAssetsColumns = [
        {
            title: 'SKU',
            dataIndex: 'skuCode',
            key: 'skuCode',
            width: '33%',
            render: (text) => (
                <Text>
                    {text || 'N/A'}
                </Text>
            ),
        },
        {
            title: 'EAN',
            dataIndex: 'eanCode',
            key: 'eanCode',
            width: '33%',
            render: (text) => (
                <Text>
                    {text || 'N/A'}
                </Text>
            ),
        },
        {
            title: 'UPC',
            dataIndex: 'upcCode',
            key: 'upcCode',
            width: '34%',
            render: (text) => (
                <Text>
                    {text || 'N/A'}
                </Text>
            ),
        },
    ];

    // Filter items based on order type and isSelected = true
    const filteredItems = useMemo(() => {
        if (!order?.list_3d_assets_results) return [];
        return order.list_3d_assets_results.filter(item => item.isSelected === true);
    }, [order]);

    const isAddAssetsOrder = order?.type === 'add_assets_to_catalogue';
    const isOrderAssetsOrder = order?.type === 'order_3d_asset';

    // Initialize displayed items when modal opens or order changes
    useEffect(() => {
        if (visible && filteredItems.length > 0) {
            const initialItems = filteredItems.slice(0, ITEMS_PER_LOAD);
            setDisplayedItems(initialItems);
            setHasMore(filteredItems.length > ITEMS_PER_LOAD);
        } else {
            setDisplayedItems([]);
            setHasMore(false);
        }
    }, [visible, filteredItems, ITEMS_PER_LOAD]);

    // Function to load more items
    const loadMoreItems = () => {
        if (loadingMore || !hasMore) return;

        setLoadingMore(true);

        // Simulate loading delay for better UX
        setTimeout(() => {
            const currentLength = displayedItems.length;
            const nextItems = filteredItems.slice(currentLength, currentLength + ITEMS_PER_LOAD);

            setDisplayedItems(prev => [...prev, ...nextItems]);
            setHasMore(currentLength + nextItems.length < filteredItems.length);
            setLoadingMore(false);
        }, 300);
    };

    // Handle scroll event
    const handleScroll = (e) => {
        const { target } = e;
        if (target.scrollTop + target.clientHeight >= target.scrollHeight - 5) {
            loadMoreItems();
        }
    };

    return (
        <Modal
            title="Order Details"
            open={visible}
            onCancel={onCancel}
            footer={null}
            width={800}
            destroyOnHidden
            centered
        >
            {order && (
                <div>
                    {/* Order Information */}
                    <div style={{ marginBottom: '24px' }}>
                        {/* Prima riga: Date/Time e Requested by */}
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '16px' }}>
                            <div>
                                <Text strong>Date/Time:</Text>
                                <br />
                                <Text>{formatTimestamp(order.timestamp)}</Text>
                            </div>
                            <div>
                                <Text strong>Requested by:</Text>
                                <br />
                                <Text>{getUserFullName(order.profile, userProfiles)}</Text>
                            </div>
                        </div>

                        {/* Seconda riga: Type e Status */}
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: currentUserRole === 'Admin' ? '16px' : '0' }}>
                            <div>
                                <Text strong>Type:</Text>
                                <br />
                                <Tag color={isOrderAssetsOrder ? 'purple' : 'blue'}>
                                    {isAddAssetsOrder ? 'Add Assets' : 'Order Assets'}
                                </Tag>
                            </div>
                            <div>
                                <Text strong>Status:</Text>
                                <br />
                                <Tag color={getStatusColour(order.status)}>
                                    {getStatusText(order.status)}
                                </Tag>
                            </div>
                        </div>

                        {/* Terza riga: Order ID (solo per Admin) */}
                        {currentUserRole === 'Admin' && (
                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                                <div>
                                    <Text strong>Order ID:</Text>
                                    <br />
                                    <Text code>
                                        {order.id || 'N/A'}
                                    </Text>
                                </div>
                                <div>
                                    {/* Spazio vuoto per mantenere l'allineamento */}
                                </div>
                            </div>
                        )}
                    </div>

                    <Divider />

                    {/* Items List */}
                    <div>
                        <Title level={4}>
                            {isAddAssetsOrder ? 'Selected Available Assets' : 'Selected Orderable Assets'}
                        </Title>

                        {filteredItems.length > 0 ? (
                            <div
                                style={{
                                    height: '40vh',
                                    overflow: 'auto',
                                    border: '1px solid #f0f0f0',
                                    borderRadius: '6px'
                                }}
                                onScroll={handleScroll}
                            >
                                <Table
                                    columns={isAddAssetsOrder ? addAssetsColumns : orderAssetsColumns}
                                    dataSource={displayedItems}
                                    rowKey={(record) => `${record.skuCode || record.eanCode || record.upcCode || Math.random().toString(36).substr(2, 9)}`}
                                    pagination={false}
                                    size="small"
                                    loading={loadingMore}
                                    footer={() => (
                                        <div style={{ textAlign: 'center', padding: '8px', color: '#666' }}>
                                            {hasMore ? (
                                                loadingMore ? (
                                                    <span>Loading more items...</span>
                                                ) : (
                                                    <span>Scroll down to load more • {displayedItems.length} of {filteredItems.length} items</span>
                                                )
                                            ) : (
                                                <span>All {filteredItems.length} items loaded</span>
                                            )}
                                        </div>
                                    )}
                                />
                            </div>
                        ) : (
                            <div style={{
                                textAlign: 'center',
                                padding: '40px',
                                color: '#999',
                                background: '#fafafa',
                                borderRadius: '6px'
                            }}>
                                <Text type="secondary">
                                    No selected items found for this order
                                </Text>
                            </div>
                        )}
                    </div>
                </div>
            )}
        </Modal>
    );
};

export default OrderDetailsModal; 